<template>
    <div class="e-card e-rounding shadow-sm">
        <PremiumBanner/>
        <h1 class="h3">{{ $t('remoteOrder.kiosk.pageTitle') }}</h1>

        <Loader v-if="loading" />
        <div v-if="!loading">
          <!-- Configuration Warnings -->
          <div v-if="getSelectedCategoriesCount() === 0" class="alert alert-warning mb-4">
            <div class="mb-2">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              <strong>{{ $t('remoteOrder.wizard.incompleteConfiguration') }}</strong>
            </div>
            <ul class="mb-0 pl-3">
              <li v-if="getSelectedCategoriesCount() === 0">{{ $t('remoteOrder.wizard.warningNoCategories') }}</li>
            </ul>
          </div>

          <div class="mb-4">
            {{ $t('remoteOrder.kiosk.howItWorks.title') }}

            <svg viewBox="0 0 470 125" fill="none" xmlns="http://www.w3.org/2000/svg" class="kiosk-illustration my-4">
              <!-- Step 1: Ordering kiosk (card x=25, icon center=85) -->
              <rect x="25" y="8" width="120" height="88" rx="10" fill="#eef2fa"/>
              <!-- Kiosk body -->
              <rect x="62" y="18" width="46" height="54" rx="3" fill="#1e3d7a"/>
              <!-- Screen -->
              <rect x="65" y="21" width="40" height="44" rx="2" fill="#dde8f8"/>
              <!-- Title bar -->
              <rect x="67" y="23" width="36" height="5" rx="1" fill="#3a6abf"/>
              <!-- Menu items -->
              <rect x="67" y="32" width="22" height="2.5" rx="1" fill="#1e3d7a" opacity="0.5"/>
              <rect x="91" y="32" width="10" height="2.5" rx="1" fill="#1e3d7a" opacity="0.3"/>
              <rect x="67" y="37" width="18" height="2.5" rx="1" fill="#1e3d7a" opacity="0.5"/>
              <rect x="87" y="37" width="12" height="2.5" rx="1" fill="#1e3d7a" opacity="0.3"/>
              <rect x="67" y="42" width="20" height="2.5" rx="1" fill="#1e3d7a" opacity="0.5"/>
              <rect x="89" y="42" width="10" height="2.5" rx="1" fill="#1e3d7a" opacity="0.3"/>
              <!-- Order button -->
              <rect x="67" y="49" width="36" height="10" rx="4" fill="#4caf50"/>
              <!-- Stand -->
              <rect x="81" y="72" width="8" height="8" rx="1" fill="#1e3d7a"/>
              <rect x="72" y="79" width="26" height="3" rx="1.5" fill="#1e3d7a"/>
              <text x="85" y="112" text-anchor="middle" font-family="system-ui,sans-serif" font-size="11" fill="#555">{{ $t('deviceMode.label.kiosk') }}</text>

              <!-- Arrow 1 -->
              <line x1="147" y1="52" x2="171" y2="52" stroke="#3a6abf" stroke-width="2" stroke-linecap="round"/>
              <polyline points="165,46 173,52 165,58" stroke="#3a6abf" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>

              <!-- Step 2: Cash register / POS (card x=175, icon center=235) -->
              <rect x="175" y="8" width="120" height="88" rx="10" fill="#eef2fa"/>
              <rect x="200" y="20" width="70" height="48" rx="4" fill="#1e3d7a"/>
              <rect x="204" y="24" width="62" height="38" rx="2" fill="#dde8f8"/>
              <rect x="208" y="29" width="28" height="2" rx="1" fill="#1e3d7a" opacity="0.4"/>
              <rect x="208" y="34" width="40" height="2" rx="1" fill="#1e3d7a" opacity="0.4"/>
              <rect x="208" y="39" width="22" height="2" rx="1" fill="#1e3d7a" opacity="0.4"/>
              <circle cx="250" cy="47" r="8" fill="#4caf50"/>
              <polyline points="245,47 249,51 255,43" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              <rect x="231" y="68" width="8" height="5" rx="1" fill="#1e3d7a"/>
              <rect x="223" y="72" width="24" height="3" rx="1.5" fill="#1e3d7a"/>
              <text x="235" y="112" text-anchor="middle" font-family="system-ui,sans-serif" font-size="11" fill="#555">{{ $t('deviceMode.label.cashRegister') }}</text>

              <!-- Arrow 2 -->
              <line x1="297" y1="52" x2="321" y2="52" stroke="#3a6abf" stroke-width="2" stroke-linecap="round"/>
              <polyline points="315,46 323,52 315,58" stroke="#3a6abf" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>

              <!-- Step 3: Kitchen display (card x=325, icon center=385) -->
              <rect x="325" y="8" width="120" height="88" rx="10" fill="#eef2fa"/>
              <rect x="362" y="23" width="46" height="32" rx="3" fill="#1e3d7a"/>
              <rect x="365" y="26" width="40" height="26" rx="1.5" fill="#dde8f8"/>
              <rect x="367" y="28" width="16" height="21" rx="1.5" fill="white"/>
              <rect x="369" y="31" width="10" height="1.5" rx="0.5" fill="#3a6abf"/>
              <rect x="369" y="34" width="8" height="1" rx="0.5" fill="#aaa" opacity="0.7"/>
              <rect x="369" y="36" width="10" height="1" rx="0.5" fill="#aaa" opacity="0.7"/>
              <rect x="369" y="40" width="12" height="3" rx="1" fill="#4caf50"/>
              <rect x="385" y="28" width="16" height="21" rx="1.5" fill="white"/>
              <rect x="387" y="31" width="10" height="1.5" rx="0.5" fill="#3a6abf"/>
              <rect x="387" y="34" width="8" height="1" rx="0.5" fill="#aaa" opacity="0.7"/>
              <rect x="387" y="36" width="10" height="1" rx="0.5" fill="#aaa" opacity="0.7"/>
              <rect x="387" y="40" width="12" height="3" rx="1" fill="#ffc107"/>
              <rect x="381" y="55" width="8" height="6" rx="1" fill="#1e3d7a"/>
              <rect x="372" y="60" width="26" height="3" rx="1.5" fill="#1e3d7a"/>
              <text x="385" y="112" text-anchor="middle" font-family="system-ui,sans-serif" font-size="11" fill="#555">{{ $t('deviceMode.label.ordersDisplay') }}</text>
            </svg>

            <p>{{ $t('remoteOrder.kiosk.howItWorks.p1') }}</p>
            <p v-html="$t('remoteOrder.kiosk.howItWorks.p2')"></p>

            <h6 class="mt-4 mb-2">{{ $t('remoteOrder.kiosk.howToSetup.title') }}</h6>
            <ol class="mb-3">
              <li>{{ $t('remoteOrder.kiosk.howToSetup.step1') }}</li>
              <li>{{ $t('remoteOrder.kiosk.howToSetup.step2') }}</li>
              <li v-html="$t('remoteOrder.kiosk.howToSetup.step3')"></li>
              <li>{{ $t('remoteOrder.kiosk.howToSetup.step4') }}</li>
            </ol>

            <p class="text-muted mb-0">{{ $t('remoteOrder.kiosk.howItWorks.note') }}</p>
          </div>

          <!-- Settings buttons -->
          <div class="row mb-4">
            <div class="col-12 col-md-6 mb-3 mb-md-0">
              <button @click="openModeSettingsModal" class="btn btn-outline-primary btn-block">
                <i class="fas fa-cog mr-2"></i>
                {{ $t('remoteOrder.buttons.modeSettings') }}
              </button>
            </div>
            <div class="col-12 col-md-6 mb-3 mb-md-0">
              <button @click="showCategorySettings = true" class="btn btn-outline-primary btn-block">
                <i class="fas fa-list mr-2"></i>
                {{ $t('remoteOrder.buttons.categorySettings') }}
              </button>
            </div>
          </div>

            <!-- Kiosk button -->
            <div class="mt-3">
              <button @click="openKioskModal" class="btn btn-outline-primary btn-block">
                <i class="fas fa-desktop mr-2"></i>
                {{ $t('remoteOrder.kiosk.showDevices') }}
              </button>
            </div>

        </div>

        <!-- Modal: Remote ordering mode -->
        <div class="e-card-backdrop" v-if="showModeSettings">
          <div class="row e-modal-row justify-content-center align-items-start">
            <div class="e-modal-card col-10 col-lg-6 bg-white shadow-sm e-rounding p-0">
              <div class="card-header e-user__header">
                <span>{{ $t('remoteOrder.buttons.modeSettings') }}</span>
                <i @click="showModeSettings = false" class="fas fa-times e-modal-close-icon"></i>
              </div>
              <div class="card-body py-5 px-4">
                <div class="mb-4 p-3 border rounded">
                  <label for="customerMessage" class="mb-3 font-weight-bold">{{ $t('remoteOrder.mode.customerMessage') }}</label>
                  <textarea v-model="customerMessage" class="form-control" id="customerMessage" rows="1"></textarea>
                  <small class="form-text text-muted">{{ $t('remoteOrder.mode.customerMessageSubtitle') }}</small>
                </div>

               <div class="mb-4 p-3 border rounded">
                 <div class="d-flex align-items-center justify-content-between">
                   <div>
                     <label class="mb-0 font-weight-bold">{{ $t('remoteOrder.mode.enableScanner') }}</label>
                     <small class="form-text text-muted">{{ $t('remoteOrder.mode.enableScannerSubtitle') }}</small>
                   </div>
                   <div class="custom-control custom-switch ml-3">
                     <input type="checkbox" class="custom-control-input" id="enableScannerSwitch" v-model="enableScanner">
                     <label class="custom-control-label" for="enableScannerSwitch"></label>
                   </div>
                 </div>
                 <div v-if="enableScanner" class="mt-3 pt-3 border-top">
                   <div class="d-flex align-items-center justify-content-between mb-3">
                     <div>
                       <label class="mb-0 font-weight-bold">{{ $t('remoteOrder.mode.enableWeightBarcode') }}</label>
                       <small class="form-text text-muted">{{ $t('remoteOrder.mode.enableWeightBarcodeSubtitle') }}</small>
                     </div>
                     <div class="custom-control custom-switch ml-3">
                       <input type="checkbox" class="custom-control-input" id="enableWeightBarcodeSwitch" v-model="enableWeightBarcode">
                       <label class="custom-control-label" for="enableWeightBarcodeSwitch"></label>
                     </div>
                   </div>
                   <div v-if="enableWeightBarcode">
                     <label class="mb-0 font-weight-bold">{{ $t('remoteOrder.mode.divideGrams') }}</label>
                     <small class="form-text text-muted d-block mb-2">{{ $t('remoteOrder.mode.divideGramsSubtitle') }}</small>
                     <select class="form-control" style="max-width: 120px;" v-model.number="divideGrams">
                       <option :value="1">1</option>
                       <option :value="10">10</option>
                       <option :value="100">100</option>
                       <option :value="1000">1000</option>
                     </select>
                   </div>
                 </div>
               </div>
               <div class="mb-4 p-3 border rounded">
                 <label class="mb-3 font-weight-bold">{{ $t('remoteOrder.mode.template') }}</label>
                 <small class="form-text text-muted mb-3">{{ $t('remoteOrder.mode.templateSubtitle') }}</small>
                 <div class="row">
                   <div class="col-12 col-md-6">
                     <div
                       @click="selectedTemplate = 'default'"
                       class="e-inner-card shadow-sm e-rounding mb-3 wizard-choice"
                       :class="{ 'wizard-choice-active': selectedTemplate === 'default' }">
                       <div class="card-body py-3 px-4">
                         <div class="d-flex align-items-center">
                           <div class="wizard-icon d-flex align-items-center justify-content-center rounded flex-shrink-0 mr-3">
                             <i class="fas fa-file-alt"></i>
                           </div>
                           <div class="flex-grow-1">
                             <h6 class="mb-1">{{ $t('remoteOrder.mode.templateDefault') }}</h6>
                             <small class="form-text text-muted">{{ $t('remoteOrder.mode.templateDefaultDesc') }}</small>
                           </div>
                           <div class="wizard-check flex-shrink-0 ml-3">
                             <i class="fas fa-check-circle"></i>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>

                   <div class="col-12 col-md-6">
                     <div
                       @click="selectedTemplate = 'template1'"
                       class="e-inner-card shadow-sm e-rounding mb-3 wizard-choice"
                       :class="{ 'wizard-choice-active': selectedTemplate === 'template1' }">
                       <div class="card-body py-3 px-4">
                         <div class="d-flex align-items-center">
                           <div class="wizard-icon d-flex align-items-center justify-content-center rounded flex-shrink-0 mr-3">
                             <i class="fas fa-moon"></i>
                           </div>
                           <div class="flex-grow-1">
                             <h6 class="mb-1">{{ $t('remoteOrder.mode.template1') }}</h6>
                             <small class="form-text text-muted">{{ $t('remoteOrder.mode.template1Desc') }}</small>
                           </div>
                           <div class="wizard-check flex-shrink-0 ml-3">
                             <i class="fas fa-check-circle"></i>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>

                   <div class="col-12 col-md-6">
                     <div
                       @click="selectedTemplate = 'template2'"
                       class="e-inner-card shadow-sm e-rounding mb-3 wizard-choice"
                       :class="{ 'wizard-choice-active': selectedTemplate === 'template2' }">
                       <div class="card-body py-3 px-4">
                         <div class="d-flex align-items-center">
                           <div class="wizard-icon d-flex align-items-center justify-content-center rounded flex-shrink-0 mr-3">
                             <i class="fas fa-sun"></i>
                           </div>
                           <div class="flex-grow-1">
                             <h6 class="mb-1">{{ $t('remoteOrder.mode.template2') }}</h6>
                             <small class="form-text text-muted">{{ $t('remoteOrder.mode.template2Desc') }}</small>
                           </div>
                           <div class="wizard-check flex-shrink-0 ml-3">
                             <i class="fas fa-check-circle"></i>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>

                   <div class="col-12 col-md-6">
                     <div
                       @click="selectedTemplate = 'template3'"
                       class="e-inner-card shadow-sm e-rounding mb-3 wizard-choice"
                       :class="{ 'wizard-choice-active': selectedTemplate === 'template3' }">
                       <div class="card-body py-3 px-4">
                         <div class="d-flex align-items-center">
                           <div class="wizard-icon d-flex align-items-center justify-content-center rounded flex-shrink-0 mr-3">
                             <i class="fas fa-leaf"></i>
                           </div>
                           <div class="flex-grow-1">
                             <h6 class="mb-1">{{ $t('remoteOrder.mode.template3') }}</h6>
                             <small class="form-text text-muted">{{ $t('remoteOrder.mode.template3Desc') }}</small>
                           </div>
                           <div class="wizard-check flex-shrink-0 ml-3">
                             <i class="fas fa-check-circle"></i>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>

                   <div class="col-12 col-md-6">
                     <div
                       @click="selectedTemplate = 'custom'"
                       class="e-inner-card shadow-sm e-rounding mb-3 wizard-choice"
                       :class="{ 'wizard-choice-active': selectedTemplate === 'custom' }">
                       <div class="card-body py-3 px-4">
                         <div class="d-flex align-items-center">
                           <div class="wizard-icon d-flex align-items-center justify-content-center rounded flex-shrink-0 mr-3">
                             <i class="fas fa-code"></i>
                           </div>
                           <div class="flex-grow-1">
                             <h6 class="mb-1">{{ $t('remoteOrder.mode.templateCustom') }}</h6>
                             <small class="form-text text-muted">{{ $t('remoteOrder.mode.templateCustomDesc') }}</small>
                           </div>
                           <div class="wizard-check flex-shrink-0 ml-3">
                             <i class="fas fa-check-circle"></i>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>

                 <div v-if="selectedTemplate === 'custom'" class="mt-3">
                   <label for="customTemplate" class="font-weight-bold">{{ $t('remoteOrder.mode.customTemplateLabel') }}</label>
                   <textarea v-model="customTemplate" class="form-control custom-template-textarea" id="customTemplate" rows="10"
                             :placeholder="$t('remoteOrder.mode.customTemplatePlaceholder')"></textarea>
                   <small class="form-text text-muted">{{ $t('remoteOrder.mode.customTemplateSubtitle') }}</small>
                 </div>
               </div>

               <div class="d-flex flex-wrap-reverse justify-content-end pt-3">
                 <button @click="showModeSettings = false" class="btn btn-outline-secondary mr-4">
                   {{ $t('button.back') }}
                 </button>
                 <button @click="saveModeSettings" class="btn btn-primary">
                   {{ $t('button.save') }}
                 </button>
               </div>
             </div>
           </div>
         </div>
       </div>

       <!-- Modal: Category settings -->
        <div class="e-card-backdrop" v-if="showCategorySettings">
          <div class="row e-modal-row justify-content-center align-items-start">
            <div class="e-modal-card col-10 col-lg-8 bg-white shadow-sm e-rounding p-0">
              <div class="card-header e-user__header">
                <span>{{ $t('remoteOrder.buttons.categorySettings') }}</span>
                <i @click="showCategorySettings = false" class="fas fa-times e-modal-close-icon"></i>
              </div>
              <div class="card-body py-5 px-4">
                <div class="form-group">
                  <label>{{ $t('remoteOrder.selectCategories') }}</label>
                  <div v-if="categories.length === 0" class="my-4">
                    <div class="text-danger"><i class="fa-solid fa-triangle-exclamation"></i> {{ $t('remoteOrder.noCategories')}}</div>
                    <a href="/#/categories">{{ $t('remoteOrder.manageCategories')}}</a>
                    ({{ $t('menu.saleSettings') }} -> {{ $t('menu.categories') }})
                  </div>
                  <div v-if="categories.length > 0" class="mb-3">
                    <button @click="selectAllCategories" class="btn btn-sm btn-outline-secondary mr-2">{{ $t('remoteOrder.category.enableAll') }}</button>
                    <button @click="deselectAllCategories" class="btn btn-sm btn-outline-secondary mr-2">{{ $t('remoteOrder.category.disableAll') }}</button>
                    <button @click="scheduleAllCategories" class="btn btn-sm btn-outline-secondary mr-2">{{ $t('remoteOrder.category.scheduleAll') }}</button>
                    <button @click="showUnifiedScheduleModal = true" class="btn btn-sm btn-outline-secondary">{{ $t('remoteOrder.category.unifiedSchedule') }}</button>
                  </div>
                  <div v-for="category in categories" v-bind:key="category.categoryId" class="row mb-3 align-items-center">
                    <div class="col-md-2">
                      <strong>{{ category.categoryName }}</strong>
                    </div>
                    <div class="col-md-4">
                      <div class="btn-group btn-group-toggle btn-group-sm w-100" data-toggle="buttons">
                        <label class="btn btn-sm" :class="category.visibility === 'invisible' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.visibility = 'invisible'">
                          {{ $t('category.visibility.invisible') }}
                        </label>
                        <label class="btn btn-sm" :class="category.visibility === 'visible' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.visibility = 'visible'">
                          {{ $t('category.visibility.visible') }}
                        </label>
                        <label class="btn btn-sm" :class="category.visibility === 'scheduled' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.visibility = 'scheduled'">
                          {{ $t('category.visibility.scheduled') }}
                        </label>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="input-group input-group-sm">
                        <div class="input-group-prepend">
                          <span class="input-group-text">{{ $t('remoteOrder.schedule.timeFrom') }}</span>
                        </div>
                        <input type="time" class="form-control" v-model="category.fromTime" :disabled="category.visibility !== 'scheduled'">
                        <div class="input-group-prepend">
                          <span class="input-group-text">{{ $t('remoteOrder.schedule.timeTo') }}</span>
                        </div>
                        <input type="time" class="form-control" v-model="category.toTime" :disabled="category.visibility !== 'scheduled'">
                      </div>
                    </div>
                  </div>
                </div>

                <div class="d-flex flex-wrap-reverse justify-content-end pt-3">
                  <button @click="showCategorySettings = false" class="btn btn-outline-secondary mr-4">
                    {{ $t('button.back') }}
                  </button>
                  <button @click="saveCategorySettings" class="btn btn-primary">
                    {{ $t('button.save') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal: Kiosk Links -->
        <div class="e-card-backdrop" v-if="showKiosk">
          <div class="row e-modal-row justify-content-center align-items-start">
            <div class="e-modal-card col-10 col-lg-8 bg-white shadow-sm e-rounding p-0">
              <div class="card-header e-user__header">
                <span>{{ $t('remoteOrder.kiosk.title') }}</span>
                <i @click="showKiosk = false" class="fas fa-times e-modal-close-icon"></i>
              </div>

              <div class="card-body py-4 px-4">
                <!-- Loading state -->
                <div v-if="kioskLoading" class="text-center py-4">
                  <i class="fas fa-spinner fa-spin fa-2x"></i>
                </div>

                <!-- No cash registers -->
                <div v-else-if="kioskDevices.length === 0" class="alert alert-info">
                  <i class="fas fa-info-circle mr-1"></i>
                  {{ $t('remoteOrder.kiosk.noDevices') }}
                </div>
                <!-- Device list -->
                <div v-else>
                  <div v-for="device in kioskDevices" :key="device.deviceId" class="border rounded p-3 mb-3">
                    <h6 class="mb-2">
                      <i :class="device.deviceMode === 'kiosk' ? 'fas fa-tablet-alt mr-1' : 'fas fa-cash-register mr-1'"></i>
                      {{ device.name }} <span class="text-muted small">({{ device.cashRegister }}) – {{ $t('deviceMode.label.' + device.deviceMode) }}</span>
                    </h6>
                    <a :href="kioskLinks[device.deviceId]" target="_blank">{{ $t('remoteOrder.kiosk.linkLabel') }}</a>
                    <div class="input-group">
                      <input type="text"
                             class="form-control form-control-sm"
                             :value="kioskLinks[device.deviceId] || '...'"
                             readonly />
                      <div class="input-group-append">
                        <button class="btn btn-sm btn-outline-secondary"
                                @click="copyToClipboard(kioskLinks[device.deviceId])"
                                :disabled="!kioskLinks[device.deviceId]">
                          <i class="fas fa-copy"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="d-flex justify-content-end pt-3 border-top">
                  <button @click="showKiosk = false" class="btn btn-outline-secondary">
                    {{ $t('button.back') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

    </div>
</template>

<script>
    import Loader from "@/components/Loader"
    import PremiumBanner from "@/components/PremiumBanner";
    import {baseURL} from '@/config/variables'

    export default {
        name: 'KioskSettings',
        components: { Loader, PremiumBanner },

        data: function () {
            return {
                loading: true,
                bu: baseURL,
                ak: localStorage.apiKey,

                // Hlavní stav komponenty
                customerMessage: "",
                categories: [],
                enableScanner: false,
                enableWeightBarcode: false,
                divideGrams: 1,
                selectedTemplate: 'default',
                customTemplate: '',

                // Stav pro modální okna
                showModeSettings: false,
                showCategorySettings: false,
                showKiosk: false,

                // Stav pro kiosek
                kioskLoading: false,
                kioskDevices: [],
                kioskLinks: {},
            }
        },

        mounted: function () {
            this.refresh();
        },

        methods: {
            refresh: function() {
                const vm = this;
                vm.loading = true;
                this.$http.post('/remote-order-settings/get-ro-settings-kiosk', {})
                    .then(function(response) {
                        vm.customerMessage = response.data.customerMessage;
                        vm.categories = response.data.categories.map(function(cat) {
                            return {
                                categoryId: cat.categoryId,
                                categoryName: cat.categoryName,
                                visibility: cat.visibility.visibility,
                                fromTime: vm.hourMinuteToTimeString(cat.visibility.from),
                                toTime: vm.hourMinuteToTimeString(cat.visibility.to)
                            };
                        });
                        vm.selectedTemplate = response.data.template || 'default';
                        vm.customTemplate = response.data.customTemplate || '';
                        vm.enableScanner = response.data.enableScanner;
                        vm.enableWeightBarcode = response.data.enableWeightBarcode;
                        vm.divideGrams = response.data.divideGrams;
                    })
                    .catch(function(error) {
                        console.error('Error getting remote order settings or bills:', error);
                    })
                    .finally(function() {
                        vm.loading = false;
                    });
            },

            saveModeSettings: function() {
                const vm = this;
                vm.loading = true;
                const categoriesToSave = vm.categories.map(function(c) {
                    return {
                        categoryId: c.categoryId,
                        visibility: {
                            visibility: c.visibility,
                            from: vm.timeStringToHourMinute(c.fromTime),
                            to: vm.timeStringToHourMinute(c.toTime)
                        }
                    };
                });
                this.$http.post('/remote-order-settings/update-ro-settings-kiosk', {
                  categories: categoriesToSave,
                  customerMessage: vm.customerMessage,
                  enableScanner: vm.enableScanner,
                  enableWeightBarcode: vm.enableWeightBarcode,
                  divideGrams: vm.divideGrams,
                  template: vm.selectedTemplate,
                  customTemplate: vm.customTemplate
                })
                    .then(function () {
                      vm.showModeSettings = false;
                      vm.refresh();
                    })
                    .catch(function (error) {
                      console.error('Error saving mode settings:', error);
                    })
                    .finally(function() {
                        vm.loading = false;
                    });
            },

            saveCategorySettings: function() {
                const vm = this;
                vm.loading = true;
                const categoriesToSave = vm.categories.map(function(c) {
                    return {
                        categoryId: c.categoryId,
                        visibility: {
                            visibility: c.visibility,
                            from: vm.timeStringToHourMinute(c.fromTime),
                            to: vm.timeStringToHourMinute(c.toTime)
                        }
                    };
                });
                this.$http.post('/remote-order-settings/update-ro-settings-kiosk', {
                  categories: categoriesToSave,
                  customerMessage: vm.customerMessage,
                  enableScanner: vm.enableScanner,
                  enableWeightBarcode: vm.enableWeightBarcode,
                  divideGrams: vm.divideGrams,
                  template: vm.selectedTemplate,
                  customTemplate: vm.customTemplate
                })
                    .then(function () {
                      vm.showCategorySettings = false;
                      vm.refresh();
                    })
                    .catch(function (error) {
                      console.error('Error saving category settings:', error);
                    })
                    .finally(function() {
                        vm.loading = false;
                    });
            },

            openKioskModal: function() {
                const vm = this;
                vm.showKiosk = true;
                vm.kioskLoading = true;
                vm.kioskDevices = [];
                vm.kioskLinks = {};
                vm.$http.post('/device/get-devices')
                    .then(function(response) {
                        vm.kioskDevices = response.data.devices.filter(function(d) {
                            return d.active && d.type!=='virtual' && (d.deviceMode === 'kiosk') && d.modelName !== null;
                        });
                        vm.kioskDevices.forEach(function(device) {
                            vm.loadKioskLink(device.deviceId);
                        });
                    })
                    .catch(function(error) {
                        console.error('Error loading devices for kiosk:', error);
                    })
                    .finally(function() {
                        vm.kioskLoading = false;
                    });
            },

            loadKioskLink: function(deviceId) {
                const vm = this;
                vm.$http.post('/remote-order-settings/get-kiosk-code-link', {
                    deviceId: deviceId
                })
                    .then(function(response) {
                        vm.$set(vm.kioskLinks, deviceId, response.data.url);
                    })
                    .catch(function(error) {
                        console.error('Error loading kiosk link for deviceId ' + deviceId + ':', error);
                    });
            },

            copyToClipboard: function(text) {
                const vm = this;
                if (text) {
                    navigator.clipboard.writeText(text).then(function() {
                        vm.$modal.cs_alert(vm.$t('remoteOrder.qrDownload.urlCopied'));
                    }).catch(function(err) {
                        console.error('Could not copy text using navigator.clipboard, falling back to execCommand.', err);
                        const textArea = document.createElement('textarea');
                        textArea.value = text;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        vm.$modal.cs_alert(vm.$t('remoteOrder.qrDownload.urlCopied'));
                    });
                }
            },

            getSelectedCategoriesCount: function() {
                return this.categories.filter(c => c.visibility === 'visible' || c.visibility === 'scheduled').length;
            },

            selectAllCategories: function() {
                this.categories.forEach(c => c.visibility = 'visible');
            },

            deselectAllCategories: function() {
                this.categories.forEach(c => c.visibility = 'invisible');
            },

            scheduleAllCategories: function() {
                this.categories.forEach(c => c.visibility = 'scheduled');
            },

          openModeSettingsModal: function() {
            this.showModeSettings = true;
          },

          timeStringToHourMinute: function(timeStr) {
            if (!timeStr) return null;
            const parts = timeStr.split(':');
            return {
              hour: parseInt(parts[0], 10),
              minute: parseInt(parts[1], 10)
            };
          },

          hourMinuteToTimeString: function(hourMinute) {
            if (!hourMinute) return '';
            const h = String(hourMinute.hour).padStart(2, '0');
            const m = String(hourMinute.minute).padStart(2, '0');
            return h + ':' + m;
          },
        },
    }
</script>

<style scoped>

  input[type="time"]:disabled {
    opacity: 0.5;
}

.custom-template-textarea {
  font-family: monospace;
  font-size: 0.875rem;
}

  .wizard-choice {
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid transparent;
  }

  .wizard-choice:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15) !important;
  }

  .wizard-choice-active {
    border-color: #19415b;
    background-color: #f0f8ff;
  }

  .wizard-icon {
    width: 50px;
    height: 50px;
    background-color: #f0f0f0;
    font-size: 1.5rem;
    color: #6c757d;
    transition: all 0.2s ease;
  }

  .wizard-choice-active .wizard-icon {
    background-color: #19415b;
    color: white;
  }

  .wizard-check {
    font-size: 1.5rem;
    color: #dee2e6;
    transition: all 0.2s ease;
  }

  .wizard-choice-active .wizard-check {
    color: #419c30;
  }


.subdomain-input-group {
  flex-wrap: nowrap;
}
.subdomain-input-group > .form-control {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
}
@media screen and (max-width: 576px) {
  .subdomain-input-group > .input-group-prepend > .input-group-text,
  .subdomain-input-group > .input-group-append > .input-group-text {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    font-size: 0.875rem;
  }
  .subdomain-input-group > .form-control {
    font-size: 0.875rem;
    padding-left: 0.4rem;
    padding-right: 0.4rem;
  }
}

.kiosk-illustration {
  display: block;
  max-width: 470px;
  width: 100%;
  margin: 0 auto;
}
</style>